import type { GeoJsonFeatureCollection, MapMarker } from '@gpx-spa/geo-domain';
import type { V2CurrentPositionLike } from '@/map/operational/v2-current-positions-markers';
import { CONVOY_PROXIMITY_RADIUS_METERS } from './constants';
import { clusterConvoyByProximity } from './cluster-by-proximity';
import { spiderfyConvoyMembers } from './spiderfy-members';
import {
  convoyClustersToMapMarkers,
  convoyMembersToDeviceMarkers,
  membersFromV2CurrentPositions,
} from './to-map-markers';
import type { ConvoyTrackerPresentation } from './presentation';
import type { ConvoyCluster } from './types';

export type ShipmentDevicesRef = {
  id: number;
  device_ids?: number[] | null;
};

export type ConvoyClustersByShipmentId = Map<number, ConvoyCluster[]>;

/**
 * Per-shipment proximity clustering for operational map paint.
 *
 * Hard rule: each shipment is clustered independently — devices from different
 * shipments never merge even if they share the same coordinates.
 */
export function buildConvoyClustersByShipment(
  shipments: readonly ShipmentDevicesRef[],
  positions: readonly V2CurrentPositionLike[],
  proximityRadiusMeters: number = CONVOY_PROXIMITY_RADIUS_METERS,
): ConvoyClustersByShipmentId {
  const byShipment: ConvoyClustersByShipmentId = new Map();
  for (const shipment of shipments) {
    const deviceIds = shipment.device_ids ?? [];
    const members = membersFromV2CurrentPositions(positions, deviceIds);
    const clusters = clusterConvoyByProximity(members, {
      radiusMeters: proximityRadiusMeters,
    });
    byShipment.set(shipment.id, clusters);
  }
  return byShipment;
}

export type BuildConvoyMarkersForShipmentsOptions = {
  /** Proximity cluster linkage radius (meters). Default CONVOY_PROXIMITY_RADIUS_METERS. */
  proximityRadiusMeters?: number;
  expandedShipmentIds?: ReadonlySet<number>;
  presentation?: ConvoyTrackerPresentation;
};

export type ConvoyLivePaint = {
  markers: MapMarker[];
  spiderfyLegs: GeoJsonFeatureCollection;
};

function emptyLegs(): GeoJsonFeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

/**
 * Live convoy paint: clustered count pins, or expanded named pins with spiderfy legs
 * when stacked. Each shipment is clustered independently.
 */
export function buildConvoyLivePaintForShipments(
  shipments: readonly ShipmentDevicesRef[],
  positions: readonly V2CurrentPositionLike[],
  options: BuildConvoyMarkersForShipmentsOptions = {},
): ConvoyLivePaint {
  const proximityRadiusMeters =
    options.proximityRadiusMeters ?? CONVOY_PROXIMITY_RADIUS_METERS;
  const expanded = options.expandedShipmentIds ?? new Set<number>();
  const byShipment = buildConvoyClustersByShipment(
    shipments,
    positions,
    proximityRadiusMeters,
  );
  const markers: MapMarker[] = [];
  const spiderfyLegs: GeoJsonFeatureCollection = emptyLegs();
  for (const shipment of shipments) {
    const markerOptions = {
      shipmentId: shipment.id,
      presentation: options.presentation,
    };
    if (expanded.has(shipment.id)) {
      const deviceIds = shipment.device_ids ?? [];
      const members = membersFromV2CurrentPositions(positions, deviceIds);
      const spiderfied = spiderfyConvoyMembers(members);
      markers.push(
        ...convoyMembersToDeviceMarkers(spiderfied.members, markerOptions),
      );
      spiderfyLegs.features.push(...spiderfied.legs.features);
      continue;
    }
    const clusters = byShipment.get(shipment.id) ?? [];
    markers.push(...convoyClustersToMapMarkers(clusters, markerOptions));
  }
  return { markers, spiderfyLegs };
}

/**
 * Build map markers for all shipments (concat). Prefer
 * {@link buildConvoyLivePaintForShipments} when spiderfy legs are needed.
 */
export function buildConvoyMarkersForShipments(
  shipments: readonly ShipmentDevicesRef[],
  positions: readonly V2CurrentPositionLike[],
  options: BuildConvoyMarkersForShipmentsOptions = {},
): MapMarker[] {
  return buildConvoyLivePaintForShipments(shipments, positions, options)
    .markers;
}
